import { useState, useEffect } from "react";

const C = {
  cream: "#F5F1EA",
  white: "#FFFFFF",
  dark: "#163C2F",
  mid: "#214D3A",
  green: "#7FB98E",
  greenPale: "#EAF2ED",
  muted: "#9B9690",
  border: "#D4CFC7",
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—";

const fmtCurrency = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—";

export default function PaymentReceipt({ supabase }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payment, setPayment] = useState(null);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get("payment_id");
    if (!paymentId) {
      setError("No payment ID found in URL.");
      setLoading(false);
      return;
    }
    loadReceipt(Number(paymentId));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const delay = (ms) => new Promise(r => setTimeout(r, ms));

  const loadReceipt = async (paymentId) => {
    setLoading(true);
    setError(null);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setError("Please sign in to view your receipt.");
      setLoading(false);
      return;
    }

    const email = session.user.email;
    let tenantRecord = null;

    const tryDirect = async () => {
      const { data } = await supabase
        .from("payments")
        .select("*")
        .eq("id", paymentId)
        .maybeSingle();
      if (!data) return null;
      const { data: ten } = await supabase
        .from("tenants")
        .select("*")
        .eq("user_id", data.user_id)
        .eq("name", data.tenant)
        .limit(1)
        .maybeSingle();
      tenantRecord = ten || {};
      return data;
    };

    const tryTenantScoped = async () => {
      if (!tenantRecord) {
        const { data: ten } = await supabase
          .from("tenants")
          .select("*")
          .eq("email", email)
          .limit(1)
          .maybeSingle();
        if (!ten) return null;
        tenantRecord = ten;
      }
      const { data } = await supabase
        .from("payments")
        .select("*")
        .eq("id", paymentId)
        .eq("user_id", tenantRecord.user_id)
        .eq("tenant", tenantRecord.name)
        .maybeSingle();
      return data;
    };

    for (let attempt = 1; attempt <= 3; attempt++) {
      const pay = await tryDirect() || await tryTenantScoped();
      if (pay) {
        setTenant(tenantRecord);
        setPayment(pay);
        setLoading(false);
        return;
      }
      if (attempt < 3) await delay(3000);
    }

    if (!tenantRecord) {
      setError("Could not find your tenant record. Please sign in with the email your landlord has on file.");
    } else {
      setError("Payment not found.");
    }
    setLoading(false);
  };

  const handlePrint = () => window.print();

  const receiptNumber = payment?.stripe_payment_intent_id
    ? payment.stripe_payment_intent_id.replace("pi_", "REC-")
    : payment?.id
      ? `REC-${String(payment.id).padStart(6, "0")}`
      : "—";

  const address = tenant
    ? [tenant.address, tenant.city, tenant.state, tenant.zip].filter(Boolean).join(", ")
    : "—";

  if (loading) return (
    <div style={{ minHeight: "100vh", background: C.cream, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, fontFamily: "'Manrope', sans-serif" }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.dark}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ fontSize: 14, color: C.muted }}>Loading your receipt…</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "100vh", background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Manrope', sans-serif", padding: 24 }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 24, color: C.dark, marginBottom: 12 }}>Something went wrong</h2>
        <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.7, marginBottom: 28 }}>{error}</p>
        <a href="/tenant" style={{
          display: "inline-block", background: C.dark, color: C.cream, border: "none", borderRadius: 999,
          padding: "12px 28px", fontWeight: 700, fontSize: 13, textDecoration: "none",
          fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.08em",
        }}>Back to Portal</a>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.cream, fontFamily: "'Manrope', 'Outfit', sans-serif", padding: "0 16px" }}>
      <style>{`
        @keyframes checkPop {
          0%   { transform: scale(0); opacity: 0; }
          60%  { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
        @media print {
          body { background: #fff !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div style={{ maxWidth: 520, margin: "0 auto", paddingTop: 48, paddingBottom: 64 }}>
        {/* Success icon */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%",
            background: C.greenPale, border: `3px solid ${C.green}`,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            animation: "checkPop 0.5s ease-out",
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={C.dark} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 32, color: C.dark, marginTop: 20, marginBottom: 6, lineHeight: 1.1,
          }}>Payment Received!</h1>
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>
            Your rent payment has been processed successfully.
          </p>
        </div>

        {/* Receipt card */}
        <div style={{
          background: C.white, border: `1px solid ${C.border}`, borderRadius: 16,
          padding: "32px 28px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img src="/leasly-logo.png" alt="Leasly" width="28" height="28" style={{ borderRadius: 6 }} />
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 20, color: C.dark, lineHeight: 1 }}>Leasly</div>
                <div style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>by Gibbs Prodigy Group</div>
              </div>
            </div>
            <span style={{
              background: C.dark + "18", color: C.dark, border: `1px solid ${C.dark}33`,
              padding: "4px 14px", borderRadius: 999, fontSize: 11, fontWeight: 700,
              letterSpacing: "0.04em", textTransform: "uppercase",
            }}>Paid</span>
          </div>

          <div style={{ height: 1, background: C.border, marginBottom: 24 }} />

          {/* Receipt details */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              ["Receipt Number", receiptNumber],
              ["Tenant", payment.tenant || "—"],
              ["Property", address],
              ["Amount Paid", fmtCurrency(payment.amount)],
              ["Payment Date", fmtDate(payment.paid)],
              ["Due Date", fmtDate(payment.due)],
              ["Payment Method", payment.payment_method || "—"],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
                <span style={{ fontSize: 12, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", flexShrink: 0 }}>{label}</span>
                <span style={{ fontSize: 14, color: C.dark, fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}>{value}</span>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: C.border, margin: "24px 0" }} />

          {/* Amount highlight */}
          <div style={{
            background: C.greenPale, border: `1px solid ${C.green}44`, borderRadius: 12,
            padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <span style={{ fontSize: 13, color: C.mid, fontWeight: 600 }}>Total Paid</span>
            <span style={{ fontSize: 24, fontWeight: 700, color: C.dark, fontFamily: "'Manrope', sans-serif" }}>
              {fmtCurrency(payment.amount)}
            </span>
          </div>

          <div style={{ height: 1, background: C.border, margin: "24px 0" }} />

          {/* Footer */}
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, margin: 0 }}>
              Gibbs Prodigy Group · Powered by Leasly
            </p>
            <p style={{ fontSize: 11, color: C.muted, marginTop: 4, opacity: 0.7 }}>
              This receipt confirms your payment. Keep it for your records.
            </p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="no-print" style={{ display: "flex", gap: 12, marginTop: 28, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={handlePrint} style={{
            background: C.white, color: C.dark, border: `1.5px solid ${C.dark}`,
            borderRadius: 999, padding: "12px 28px", fontWeight: 700, fontSize: 13,
            cursor: "pointer", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase",
            letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 8,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
            </svg>
            Print Receipt
          </button>
          <a href="/tenant" style={{
            display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none",
            background: C.dark, color: C.cream, border: "none", borderRadius: 999,
            padding: "12px 28px", fontWeight: 700, fontSize: 13,
            fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.08em",
          }}>
            Back to Portal
          </a>
        </div>
      </div>
    </div>
  );
}
